import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PortalShell } from "@/pages/portal/PortalShell";
import { ScoreRing, ScoreBar } from "@/components/portal/ScoreRing";
import { Chat } from "@/components/portal/Chat";
import { CallsPanel } from "@/components/portal/CallsPanel";
import { DocVault } from "@/components/portal/DocVault";
import { TeamPanel } from "@/components/portal/TeamPanel";
import { AIProcessing } from "@/components/portal/AIProcessing";
import { ReportCenter } from "@/components/portal/ReportCenter";
import { ValuationEngine } from "@/components/portal/ValuationEngine";
import { Sparkles, CreditCard, Send, RefreshCw, FileText, Plus } from "lucide-react";

const TABS = ["Overview", "AI Validation", "Feasibility", "Valuation", "Roadmap", "Services", "Team", "Documents", "Messages", "AI Advisor", "Activity", "Reports"];

const fmtL = (v) => (v == null ? "—" : `₹${v}L`);

function Section({ title, children, testid }) {
  return (
    <div className="glass rounded-xl p-5 sm:p-6" data-testid={testid}>
      <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#00B0FF] uppercase mb-3">{title}</p>
      {children}
    </div>
  );
}

function BulletList({ items, color = "#FF8800" }) {
  if (!items?.length) return null;
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300 leading-relaxed">
          <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: color }} />
          {it}
        </li>
      ))}
    </ul>
  );
}

const CATEGORY_LABELS = {
  market_need: "Market Need", business_model: "Business Model", competition: "Competition",
  scalability: "Scalability", execution: "Execution", revenue_potential: "Revenue Potential",
  brand_readiness: "Brand", technology_fit: "Technology",
};

function ValidationTab({ startup, onRevalidate, revalidating }) {
  const a = startup.analysis;
  if (startup.ai_status === "analyzing") {
    return (
      <AIProcessing
        testid="validation-analyzing"
        title="AI is analyzing your startup"
        subtitle="The page updates automatically when your score is ready."
      />
    );
  }
  if (startup.ai_status === "error" && !a) {
    return (
      <div className="glass rounded-2xl p-14 text-center" data-testid="validation-error">
        <p className="font-display text-lg font-bold text-white mb-2">AI analysis could not be completed</p>
        <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">The analysis failed or timed out. Please try again — it usually finishes in under a minute.</p>
        <Button onClick={onRevalidate} disabled={revalidating} data-testid="validation-revalidate" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
          <RefreshCw className={`w-4 h-4 mr-2 ${revalidating ? "animate-spin" : ""}`} /> Run Again
        </Button>
      </div>
    );
  }
  if (!a) {
    return <div className="glass rounded-2xl p-14 text-center text-slate-400" data-testid="validation-empty">Submit your startup to run AI validation.</div>;
  }
  return (
    <div className="space-y-5" data-testid="validation-report">
      <div className="glass rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-8">
        <ScoreRing score={a.score} size={150} label="Idea Validation Score" testid="validation-score" />
        <div className="flex-1 text-center sm:text-left">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#00E676]/10 border border-[#00E676]/40 text-[#00E676] font-mono-tech text-xs tracking-[0.18em] uppercase" data-testid="validation-verdict">
            {a.verdict}
          </span>
          <p className="mt-3 text-xs text-slate-500 font-mono-tech tracking-wider uppercase">{a.disclaimer}</p>
          <Button onClick={onRevalidate} disabled={revalidating} variant="outline" data-testid="validation-revalidate" className="mt-4 border-white/15 text-slate-300 hover:bg-white/5">
            <RefreshCw className={`w-4 h-4 mr-2 ${revalidating ? "animate-spin" : ""}`} /> Recalculate
          </Button>
        </div>
      </div>
      <Section title="Score Breakdown" testid="validation-categories">
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
          {Object.entries(a.categories || {}).map(([k, v]) => (
            <ScoreBar key={k} label={CATEGORY_LABELS[k] || k} value={v} testid={`validation-cat-${k}`} />
          ))}
        </div>
      </Section>
      <div className="grid md:grid-cols-2 gap-5">
        <Section title="Why this idea can work" testid="validation-strengths"><BulletList items={a.why_it_can_work} color="#00E676" /></Section>
        <Section title="Key risks" testid="validation-risks"><BulletList items={a.key_risks} color="#FF8800" /></Section>
        <Section title="What must be improved" testid="validation-improve"><BulletList items={a.must_improve} color="#00B0FF" /></Section>
        <Section title="Recommended next steps" testid="validation-next"><BulletList items={a.recommended_next_steps} color="#00E676" /></Section>
      </div>
      {(a.competitors || []).length > 0 && (
        <Section title="Competitor Intelligence" testid="validation-competitors">
          <div className="grid sm:grid-cols-2 gap-3">
            {a.competitors.map((c, i) => (
              <div key={i} className="rounded-lg border border-white/10 p-4">
                <p className="font-display font-semibold text-white text-sm">{c.name}</p>
                <p className="text-[10px] font-mono-tech text-[#00B0FF] uppercase tracking-wider mt-0.5">{c.category}</p>
                <p className="text-xs text-slate-400 mt-2"><span className="text-[#00E676]">Strengths:</span> {c.strengths}</p>
                <p className="text-xs text-slate-400 mt-1"><span className="text-[#FF8800]">Weaknesses:</span> {c.weaknesses}</p>
                <p className="text-xs text-slate-300 mt-1"><span className="text-slate-500">Differentiate:</span> {c.differentiation}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[10px] text-slate-600 font-mono-tech">Competitor information is AI-generated from general market knowledge — verify independently before decisions.</p>
        </Section>
      )}
    </div>
  );
}

const FEAS_LABELS = {
  executive_summary: "Executive Summary", business_concept: "Business Concept", problem_solution_fit: "Problem–Solution Fit",
  market_opportunity: "Market Opportunity", target_customers: "Target Customers", industry_overview: "Industry Overview",
  competitor_analysis: "Competitor Analysis", competitive_advantage: "Competitive Advantage", revenue_model: "Revenue Model",
  business_model: "Business Model", operational_requirements: "Operational Requirements", technology_requirements: "Technology Requirements",
  human_resource_requirements: "Human Resources", infrastructure_requirements: "Infrastructure", regulatory_requirements: "Regulatory Requirements",
  investment_requirement: "Investment Requirement", monthly_operating_expenses: "Monthly Operating Expenses", revenue_projection: "Revenue Projection",
  gross_margin_estimate: "Gross Margin Estimate", net_profit_estimate: "Net Profit Estimate", break_even_estimate: "Break-even Estimate",
  recommended_launch_strategy: "Recommended Launch Strategy",
};

function FeasibilityTab({ startup, onGenerate, generating }) {
  const f = startup.feasibility;
  if (startup.feasibility_status === "analyzing") {
    return (
      <AIProcessing
        testid="feasibility-analyzing"
        title="Preparing your feasibility report"
        subtitle="Market, operations, finance and risk — compiled section by section."
        stages={["Reading your profile...", "Sizing the market...", "Modelling costs & pricing...", "Estimating break-even...", "Assessing risks...", "Compiling the report..."]}
      />
    );
  }
  if (startup.feasibility_status === "error" && !f) {
    return (
      <div className="glass rounded-2xl p-14 text-center" data-testid="feasibility-error">
        <p className="font-display text-lg font-bold text-white mb-2">Report generation could not be completed</p>
        <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">The feasibility report failed or timed out. Please try again.</p>
        <Button onClick={onGenerate} disabled={generating} data-testid="feasibility-regenerate" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
          <RefreshCw className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`} /> Try Again
        </Button>
      </div>
    );
  }
  if (!f) {
    return (
      <div className="glass rounded-2xl p-14 text-center" data-testid="feasibility-empty">
        <FileText className="w-10 h-10 text-slate-600 mx-auto mb-4" />
        <p className="font-display text-lg font-bold text-white mb-2">Feasibility Report</p>
        <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">Generate a professional 25-section AI feasibility analysis for this startup.</p>
        <Button onClick={onGenerate} disabled={generating || !startup.analysis} data-testid="feasibility-generate" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
          <Sparkles className="w-4 h-4 mr-2" /> {startup.analysis ? "Generate Feasibility Report" : "Run AI Validation First"}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="feasibility-report">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs text-slate-500 font-mono-tech tracking-wider uppercase">{f.disclaimer}</p>
        <div className="flex gap-2.5">
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="feasibility-export" className="border-white/15 text-slate-300 hover:bg-white/5">Export / Print PDF</Button>
          <Button variant="outline" size="sm" onClick={onGenerate} disabled={generating} data-testid="feasibility-regenerate" className="border-white/15 text-slate-300 hover:bg-white/5">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${generating ? "animate-spin" : ""}`} /> Regenerate
          </Button>
        </div>
      </div>
      {Object.entries(FEAS_LABELS).map(([key, label]) =>
        f[key] ? (
          <Section key={key} title={label} testid={`feasibility-${key}`}>
            <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{f[key]}</p>
          </Section>
        ) : null
      )}
      {[["major_risks", "Major Risks", "#FF8800"], ["risk_mitigation", "Risk Mitigation", "#00B0FF"], ["growth_opportunities", "Growth Opportunities", "#00E676"]].map(([key, label, color]) =>
        f[key]?.length ? (
          <Section key={key} title={label} testid={`feasibility-${key}`}><BulletList items={f[key]} color={color} /></Section>
        ) : null
      )}
    </div>
  );
}

function ValuationTab({ startup, onChanged }) {
  const a = startup.analysis;
  if (!a) return <div className="glass rounded-2xl p-14 text-center text-slate-400">Run AI validation to see estimates.</div>;
  return (
    <div className="space-y-5" data-testid="valuation-tab">
      <div className="grid sm:grid-cols-3 gap-5">
        <div className="glass rounded-xl p-6 text-center" data-testid="valuation-range">
          <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#00B0FF] uppercase mb-2">Estimated Valuation</p>
          <p className="font-display text-2xl font-extrabold text-gradient-saffron">{fmtL(a.valuation_low_lakhs)} – {fmtL(a.valuation_high_lakhs)}</p>
          <p className="text-[10px] text-slate-500 mt-2 font-mono-tech">{a.valuation_method}</p>
        </div>
        <div className="glass rounded-xl p-6 text-center" data-testid="investment-range">
          <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#00B0FF] uppercase mb-2">Estimated Investment</p>
          <p className="font-display text-2xl font-extrabold text-white">{fmtL(a.investment_low_lakhs)} – {fmtL(a.investment_high_lakhs)}</p>
        </div>
        <div className="glass rounded-xl p-6 text-center" data-testid="expense-range">
          <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#00B0FF] uppercase mb-2">Est. Monthly Burn</p>
          <p className="font-display text-2xl font-extrabold text-white">{fmtL(a.monthly_expense_low_lakhs)} – {fmtL(a.monthly_expense_high_lakhs)}</p>
        </div>
      </div>
      <Section title="Valuation assumptions" testid="valuation-assumptions"><BulletList items={a.valuation_assumptions} color="#00B0FF" /></Section>
      <Section title="Investment assumptions" testid="investment-assumptions"><BulletList items={a.investment_assumptions} color="#FF8800" /></Section>
      <p className="text-[11px] text-slate-500 leading-relaxed" data-testid="valuation-disclaimer">
        {a.disclaimer} This is an AI-assisted indicative valuation estimate — not a certified valuation. Consult a qualified valuation professional for formal valuation.
      </p>
      {(startup.score_history || []).length > 1 && (
        <Section title="Score history" testid="score-history">
          <div className="flex items-end gap-3 h-32">
            {startup.score_history.map((h, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5 flex-1">
                <span className="text-[10px] font-mono-tech text-slate-400">{h.score}</span>
                <div className="w-full rounded-t bg-gradient-to-t from-[#FF8800]/60 to-[#00E676]/60" style={{ height: `${Math.max(6, h.score)}%` }} />
                <span className="text-[9px] font-mono-tech text-slate-600">{h.date?.slice(5)}</span>
              </div>
            ))}
          </div>
        </Section>
      )}
      <ValuationEngine startup={startup} onChanged={onChanged} />
    </div>
  );
}

function RoadmapTab({ startup }) {
  const roadmap = startup.analysis?.roadmap || [];
  if (!roadmap.length) return <div className="glass rounded-2xl p-14 text-center text-slate-400">Your personalized roadmap appears after AI validation.</div>;
  return (
    <div className="space-y-4" data-testid="roadmap-tab">
      {roadmap.map((p, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: i * 0.05 }}
          className="glass rounded-xl p-5"
          data-testid={`roadmap-phase-${i}`}
        >
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <p className="font-display font-bold text-white"><span className="text-[#FF8800] font-mono-tech text-xs mr-2">PHASE {i + 1}</span>{p.phase}</p>
            <div className="flex gap-2">
              {p.duration && <span className="text-[10px] font-mono-tech px-2.5 py-1 rounded-full border border-[#00B0FF]/40 text-[#00B0FF]">{p.duration}</span>}
              {p.specialist && <span className="text-[10px] font-mono-tech px-2.5 py-1 rounded-full border border-[#00E676]/40 text-[#00E676]">{p.specialist}</span>}
            </div>
          </div>
          <BulletList items={p.tasks} color="#00B0FF" />
          {p.cost_note && <p className="mt-3 text-xs text-slate-500 font-mono-tech">Est. cost: {p.cost_note}</p>}
        </motion.div>
      ))}
    </div>
  );
}

function ServicesTab({ startup, orders, onChanged }) {
  const [recs, setRecs] = useState(null);
  const [paying, setPaying] = useState("");
  const canOrder = (startup.member_role || "owner") === "owner";

  useEffect(() => {
    api.get(`/portal/startups/${startup.id}/recommendations`).then((r) => setRecs(r.data)).catch(() => setRecs([]));
  }, [startup.id]);

  const loadRazorpay = () =>
    new Promise((res) => {
      if (window.Razorpay) return res(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => res(true);
      s.onerror = () => res(false);
      document.body.appendChild(s);
    });

  const addToPlan = async (serviceId) => {
    try {
      await api.post("/portal/orders", { startup_id: startup.id, service_id: serviceId });
      toast.success("Service added to your plan — complete payment to activate");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pay = async (order) => {
    setPaying(order.id);
    try {
      const res = await api.post(`/portal/orders/${order.id}/pay`);
      if (!res.data.available) {
        toast.info(res.data.message);
        return;
      }
      const loaded = await loadRazorpay();
      if (!loaded) {
        toast.error("Could not load payment gateway. Check your connection.");
        return;
      }
      const d = res.data;
      const rzp = new window.Razorpay({
        key: d.key_id, amount: d.amount, currency: d.currency, order_id: d.order_id,
        name: d.name, description: d.description, prefill: d.prefill,
        theme: { color: "#FF8800" },
        handler: async (resp) => {
          try {
            await api.post(`/portal/orders/${order.id}/verify`, resp);
            toast.success("Payment confirmed — service activated!");
            onChanged();
          } catch (err) {
            toast.error(formatApiError(err));
          }
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPaying("");
    }
  };

  const downloadInvoice = async (o) => {
    try {
      const res = await api.get(`/portal/orders/${o.id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `LaunchLab-Receipt-${o.id.slice(0, 8)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const myOrders = orders.filter((o) => o.startup_id === startup.id);

  return (
    <div className="space-y-8" data-testid="services-tab">
      <div>
        <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#00B0FF] uppercase mb-4">Recommended for this startup</p>
        {recs === null ? (
          <div className="flex justify-center py-8"><div className="w-7 h-7 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
        ) : recs.length === 0 ? (
          <div className="glass rounded-xl p-8 text-center text-slate-400 text-sm">Service recommendations appear once the services catalog is configured.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="recommendations-grid">
            {recs.map(({ service: sv, reason }) => (
              <div key={sv.id} className="glass rounded-xl p-5" data-testid={`recommendation-${sv.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display font-semibold text-white">{sv.name}</p>
                    <p className="text-[10px] font-mono-tech tracking-wider text-[#00B0FF] uppercase mt-0.5">{sv.category} · {sv.specialist_category}</p>
                  </div>
                  <p className="font-display font-bold text-gradient-saffron whitespace-nowrap">₹{sv.price.toLocaleString("en-IN")}</p>
                </div>
                <p className="text-xs text-slate-400 mt-2.5 leading-relaxed">{sv.description}</p>
                <p className="text-xs text-[#00E676] mt-2 italic">Why: {reason}</p>
                {canOrder && (
                  <Button size="sm" onClick={() => addToPlan(sv.id)} data-testid={`add-service-${sv.id}`} className="mt-4 bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Add to Plan
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#00B0FF] uppercase mb-4">My services</p>
        {myOrders.length === 0 ? (
          <div className="glass rounded-xl p-8 text-center text-slate-400 text-sm">No services in your plan yet.</div>
        ) : (
          <div className="space-y-3" data-testid="my-orders">
            {myOrders.map((o) => {
              const doneSteps = (o.workflow || []).filter((w) => w.status === "done").length;
              return (
                <div key={o.id} className="glass rounded-xl p-5" data-testid={`order-row-${o.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <p className="font-display font-semibold text-white">{o.service_name}</p>
                        <span className={`text-[10px] font-mono-tech px-2 py-0.5 rounded-full border ${
                          o.status === "completed" ? "border-[#00E676]/40 text-[#00E676]"
                          : o.status === "payment_pending" ? "border-[#FF8800]/40 text-[#FF8800]"
                          : "border-[#00B0FF]/40 text-[#00B0FF]"
                        }`}>{o.status.replace(/_/g, " ").toUpperCase()}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        ₹{o.total.toLocaleString("en-IN")} (incl. GST) · {o.specialist_name ? `Specialist: ${o.specialist_name}` : "Specialist: pending assignment"}
                      </p>
                      {(o.workflow || []).length > 0 && o.status !== "payment_pending" && (
                        <p className="text-[11px] font-mono-tech text-slate-400 mt-1.5">{doneSteps}/{o.workflow.length} steps completed</p>
                      )}
                    </div>
                    {o.status === "payment_pending" && canOrder && (
                      <Button onClick={() => pay(o)} disabled={paying === o.id} data-testid={`pay-order-${o.id}`} className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold shrink-0">
                        <CreditCard className="w-4 h-4 mr-2" /> {paying === o.id ? "Opening..." : `Pay ₹${o.total.toLocaleString("en-IN")}`}
                      </Button>
                    )}
                    {o.status !== "payment_pending" && (
                      <Button variant="outline" onClick={() => downloadInvoice(o)} data-testid={`invoice-${o.id}`} className="border-white/15 text-slate-300 hover:bg-white/5 shrink-0">
                        <FileText className="w-4 h-4 mr-2" /> Receipt
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function MessagesTab({ startup, orders, myId }) {
  const active = orders.filter((o) => o.startup_id === startup.id && o.status !== "payment_pending");
  const [selected, setSelected] = useState(null);
  const current = active.find((o) => o.id === selected) || active[0];
  if (active.length === 0) {
    return <div className="glass rounded-2xl p-14 text-center text-slate-400">Chat opens once a service is active and a specialist is assigned.</div>;
  }
  return (
    <div data-testid="messages-tab">
      {active.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {active.map((o) => (
            <button
              key={o.id}
              data-testid={`chat-order-${o.id}`}
              onClick={() => setSelected(o.id)}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${current?.id === o.id ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]" : "glass text-slate-300"}`}
            >
              {o.service_name}
            </button>
          ))}
        </div>
      )}
      {current && (
        <div className="space-y-5">
          <CallsPanel orderId={current.id} myId={myId} testid="order-calls" />
          <Chat orderId={current.id} testid="order-chat" />
        </div>
      )}
    </div>
  );
}

function AdvisorTab({ startup }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/portal/startups/${startup.id}/advisor/history`).then((r) => setMessages(r.data)).catch(() => {});
  }, [startup.id]);

  const ask = async (e, preset) => {
    e?.preventDefault();
    const msg = preset || input.trim();
    if (!msg) return;
    setSending(true);
    setInput("");
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", text: msg, created_at: new Date().toISOString() }]);
    try {
      const res = await api.post(`/portal/startups/${startup.id}/advisor`, { message: msg });
      setMessages((m) => [...m, { id: `ai-${Date.now()}`, role: "assistant", text: res.data.reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const SUGGESTIONS = ["What should I do next?", "How much investment do I need?", "What registrations do I need?", "Who are my competitors?", "How can I improve my score?"];

  return (
    <div className="glass rounded-2xl flex flex-col h-[520px]" data-testid="advisor-tab">
      <div className="px-5 py-3.5 border-b border-white/10">
        <p className="font-display font-semibold text-white text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FF8800]" /> LaunchLab AI Advisor</p>
        <p className="text-[10px] font-mono-tech tracking-widest text-slate-500 uppercase">Answers use your startup profile · estimates are not guarantees</p>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="advisor-messages">
        {messages.length === 0 && (
          <div className="py-6">
            <p className="text-center text-xs text-slate-500 mb-4">Ask anything about your startup journey.</p>
            <div className="flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={(e) => ask(e, s)} data-testid={`advisor-suggestion-${s.slice(0, 12).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="glass px-3.5 py-2 rounded-full text-xs text-slate-300 hover:text-white hover:border-[#FF8800]/50 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user" ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]" : "glass text-slate-200"
            }`}>
              {m.text}
            </div>
          </div>
        ))}
        {sending && <p className="text-xs text-slate-500 animate-pulse">AI Advisor is thinking...</p>}
      </div>
      <form onSubmit={ask} className="p-3 border-t border-white/10 flex items-center gap-2">
        <input
          data-testid="advisor-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask the AI Advisor..."
          className="flex-1 bg-[#06080E] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-[#FF8800]"
        />
        <Button type="submit" size="icon" disabled={sending} data-testid="advisor-send" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] shrink-0">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}

function OverviewTab({ startup }) {
  const a = startup.answers || {};
  const rows = [
    ["Industry", a.industry], ["Stage", a.current_stage], ["Location", a.business_location],
    ["Target customer", a.target_customer], ["Revenue models", (a.revenue_models || []).join(", ")],
    ["Planned investment", a.planned_investment], ["Expected launch", a.launch_date],
  ].filter(([, v]) => v);
  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="overview-tab">
      <Section title="Problem" testid="overview-problem"><p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{a.problem || "—"}</p></Section>
      <Section title="Solution" testid="overview-solution"><p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{a.solution || "—"}</p></Section>
      <Section title="Snapshot" testid="overview-snapshot">
        <div className="space-y-2.5">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 text-sm">
              <span className="text-slate-500">{k}</span>
              <span className="text-slate-200 text-right">{v}</span>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Differentiation" testid="overview-differentiation"><p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{a.differentiation || "—"}</p></Section>
    </div>
  );
}

function ActivityTab({ startup }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get(`/portal/startups/${startup.id}/activity`).then((r) => setItems(r.data)).catch(() => {});
  }, [startup.id]);
  return (
    <div className="glass rounded-2xl p-6" data-testid="activity-tab">
      {items.length === 0 ? (
        <p className="text-center text-slate-500 text-sm py-8">No activity yet.</p>
      ) : (
        <div className="relative pl-6 space-y-5 before:absolute before:left-1.5 before:top-2 before:bottom-2 before:w-px before:bg-white/10">
          {items.map((it) => (
            <div key={it.id} className="relative" data-testid={`activity-item-${it.id}`}>
              <span className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-[#FF8800] shadow-[0_0_10px_rgba(255,136,0,0.5)]" />
              <p className="text-sm text-slate-200">{it.text}</p>
              <p className="text-[10px] text-slate-500 font-mono-tech mt-0.5">
                {it.actor} · {new Date(it.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StartupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [startup, setStartup] = useState(null);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("Overview");
  const [revalidating, setRevalidating] = useState(false);
  const [generatingFeas, setGeneratingFeas] = useState(false);

  const load = useCallback(() => {
    api.get(`/portal/startups/${id}`).then((r) => setStartup(r.data)).catch(() => navigate("/dashboard"));
    api.get("/portal/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, [id, navigate]);

  useEffect(() => {
    api.get("/portal/auth/me").then((r) => {
      if (r.data.role !== "founder") { navigate("/portal"); return; }
      setUser(r.data);
      load();
    }).catch(() => navigate("/portal"));
  }, [id, navigate, load]);

  useEffect(() => {
    if (startup?.ai_status === "analyzing" || startup?.feasibility_status === "analyzing") {
      const t = setTimeout(load, 8000);
      return () => clearTimeout(t);
    }
  }, [startup, load]);

  useEffect(() => {
    if (!startup) return;
    const sid = sessionStorage.getItem("ll_claim_service");
    if (!sid || (startup.member_role || "owner") !== "owner") return;
    sessionStorage.removeItem("ll_claim_service");
    api.post("/portal/orders", { startup_id: startup.id, service_id: sid })
      .then(() => {
        toast.success("Your selected service was added to your plan — complete payment to activate");
        setTab("Services");
        load();
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startup?.id]);

  const revalidate = async () => {
    setRevalidating(true);
    try {
      await api.post(`/portal/startups/${id}/revalidate`);
      toast.info("AI is re-analyzing your startup...");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRevalidating(false);
    }
  };

  const generateFeasibility = async () => {
    setGeneratingFeas(true);
    try {
      await api.post(`/portal/startups/${id}/feasibility`);
      toast.info("Feasibility report is being prepared...");
      setTimeout(load, 8000);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGeneratingFeas(false);
    }
  };

  if (!user || !startup) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>;
  }

  return (
    <PortalShell user={user} title={startup.name}>
      <div data-testid="startup-detail">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <Link to="/dashboard" className="text-xs text-slate-500 hover:text-[#FF8800] transition-colors">← All startups</Link>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white mt-1.5" data-testid="startup-detail-name">
              {startup.name} {startup.brand_name && <span className="text-slate-500 font-normal text-lg">· {startup.brand_name}</span>}
            </h1>
            <p className="text-xs text-slate-500 mt-1">
              {startup.answers?.industry || "Startup"} · {startup.status === "submitted" ? "Submitted" : "Draft"}
            </p>
          </div>
          {startup.status === "draft" && (
            <Link to={`/dashboard/new/${startup.id}`} data-testid="continue-wizard">
              <Button className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">Continue Questionnaire</Button>
            </Link>
          )}
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-6" role="tablist" data-testid="startup-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              data-testid={`startup-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                tab === t ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]" : "glass text-slate-400 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "Overview" && <OverviewTab startup={startup} />}
        {tab === "AI Validation" && <ValidationTab startup={startup} onRevalidate={revalidate} revalidating={revalidating} />}
        {tab === "Feasibility" && <FeasibilityTab startup={startup} onGenerate={generateFeasibility} generating={generatingFeas} />}
        {tab === "Valuation" && <ValuationTab startup={startup} onChanged={load} />}
        {tab === "Roadmap" && <RoadmapTab startup={startup} />}
        {tab === "Services" && <ServicesTab startup={startup} orders={orders} onChanged={load} />}
        {tab === "Team" && <TeamPanel startup={startup} />}
        {tab === "Documents" && <DocVault startupId={startup.id} testid="startup-docvault" />}
        {tab === "Messages" && <MessagesTab startup={startup} orders={orders} myId={user.id} />}
        {tab === "AI Advisor" && <AdvisorTab startup={startup} />}
        {tab === "Activity" && <ActivityTab startup={startup} />}
        {tab === "Reports" && <ReportCenter startup={startup} />}
      </div>
    </PortalShell>
  );
}
